/**
 * The single place the UI talks to the server.
 *
 * Base URL is empty by default, which means "same origin" - the deployment where the API
 * serves the built React app out of its own wwwroot. Set VITE_API_BASE_URL to point at a
 * separate API origin for the two-site IIS layout.
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

const TOKEN_KEY = 'ccms.token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

/** One message per field, keyed by the form's own field name. */
export type FieldErrors = Record<string, string>;

/** Thrown for any non-2xx response, carrying the server's own message. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /**
     * Per-field messages from a 400, so a form can put each one against the input that
     * caused it instead of showing only the generic title.
     */
    public readonly fieldErrors: FieldErrors = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Fired when the server rejects the session, so the app can drop it and bounce to /login.
 *
 * `request` cannot navigate on its own - it is a plain module with no router and no auth
 * context - and throwing alone is not enough: every screen catches its own errors, so an
 * expired session used to surface as an error panel on whatever page happened to be open,
 * with the user still "signed in" as far as AuthProvider knew. AuthProvider subscribes here
 * and clears the user, which is what lets RequireAuth redirect.
 *
 * Only a 401 fires this. A failed sign-in answers 400 or 404, so a wrong password never
 * looks like an expired session.
 */
type SessionExpiredHandler = () => void;

const sessionExpiredHandlers = new Set<SessionExpiredHandler>();

/** Subscribe to session expiry. Returns the unsubscribe function. */
export function onSessionExpired(handler: SessionExpiredHandler): () => void {
  sessionExpiredHandlers.add(handler);
  return () => {
    sessionExpiredHandlers.delete(handler);
  };
}

/** Raised when the token is missing or expired, so the app can bounce to /login. */
export class UnauthorizedError extends ApiError {
  constructor() {
    super(401, 'Your session has expired. Please sign in again.');
    this.name = 'UnauthorizedError';
  }
}

/**
 * ModelState key -> form field name.
 *
 * ASP.NET reports a nested [FromBody] model by its path: DataAnnotations failures arrive as
 * `Student.StudentMobileNo`, and System.Text.Json binding failures as `$.student.gradeId`.
 * Forms name their inputs after the leaf, so the leaf is what we keep.
 */
function fieldNameOf(key: string): string {
  const leaf = key.replace(/^\$\./, '').split('.').pop() ?? key;
  // Strip an array index such as `Courses[2]`, which no input is named after.
  const name = leaf.replace(/\[\d+\]$/, '');
  return name.charAt(0).toLowerCase() + name.slice(1);
}

/**
 * Pulls ValidationProblemDetails.errors apart into one message per field.
 *
 * Without this the UI only ever saw the envelope's title - "One or more validation errors
 * occurred." - which tells the user that something is wrong but not what, or where.
 */
function parseFieldErrors(errors: unknown): FieldErrors {
  if (!errors || typeof errors !== 'object') return {};

  const result: FieldErrors = {};

  for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
    const messages = (Array.isArray(value) ? value : [value])
      .filter((m): m is string => typeof m === 'string' && m.trim() !== '');

    if (messages.length === 0) continue;

    // A key of "" is a model-level error with no field to attach it to.
    const field = key === '' ? '_' : fieldNameOf(key);
    result[field] = messages.join(' ');
  }

  return result;
}

type Options = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
};

async function request<T>(path: string, options: Options = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;

  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (response.status === 401) {
    setToken(null);

    // Tell the app before the throw. Screens catch the error and render it; only this gets
    // the user back to the sign-in page instead of leaving them on a dead one.
    sessionExpiredHandlers.forEach((handler) => handler());

    throw new UnauthorizedError();
  }

  if (response.status === 403) {
    throw new ApiError(403, 'You do not have permission to do that.');
  }

  if (!response.ok) {
    // The API returns { message } for business rule rejections. That text is carried over
    // verbatim from the old system, so users see the wording they are used to.
    let message = `Request failed (${response.status}).`;
    let fieldErrors: FieldErrors = {};

    try {
      const problem = await response.json();
      fieldErrors = parseFieldErrors(problem?.errors);

      if (problem?.message) {
        message = problem.message;
      } else {
        const details = Object.values(fieldErrors);

        // Prefer the specific rule over the envelope's title. "One or more validation errors
        // occurred." is true of every 400 and useful on none of them; the field's own message
        // says what to change. The fields highlight themselves, so the toast just leads.
        if (details.length === 1) message = details[0];
        else if (details.length > 1) message = `${details.length} fields need attention: ${details[0]}`;
        else if (problem?.title) message = problem.title;
      }
    } catch {
      /* not JSON; keep the generic message */
    }

    throw new ApiError(response.status, message, fieldErrors);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/** Mirrors PagedResult<T> on the server. */
export type Paged<T> = {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
};

export function toQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.append(key, String(value));
  }

  const query = search.toString();
  return query ? `?${query}` : '';
}
